import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Users, Car, FileText, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Edit, Trash2, Eye, Plus, Search, X } from 'lucide-react';
import api from '../../utils/api';

interface Client {
  id: number;
  email: string;
  raison_social: string | null;
  first_name: string;
  last_name: string;
  phone: string | null;
  address: string | null;
  vehicles_count: number;
  service_orders_count: number;
}

export default function Clients() {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [lazyParams, setLazyParams] = useState({
    first: 0,
    rows: 10,
    page: 1,
    sortField: null as string | null,
    sortOrder: null as 'asc' | 'desc' | null,
    filters: {} as Record<string, any>
  });
  const [globalFilter, setGlobalFilter] = useState('');
  const [dialogVisible, setDialogVisible] = useState(false);
  const [editingClient, setEditingClient] = useState<Partial<Client> | null>(null);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [clientToDelete, setClientToDelete] = useState<Client | null>(null);
  const [totalClients, setTotalClients] = useState(0);
  const [totalVehicles, setTotalVehicles] = useState(0);
  const [totalOrders, setTotalOrders] = useState(0);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [toast, setToast] = useState({ visible: false, severity: '', message: '' });

  const rowsPerPageOptions = [10, 20, 50];

  useEffect(() => {
    fetchClients();
    fetchStatistics();
  }, [lazyParams]);

  const fetchClients = async () => {
    try {
      setLoading(true);
      const response = await api.get('/clients', {
        params: {
          page: lazyParams.page,
          per_page: lazyParams.rows,
          sort_field: lazyParams.sortField,
          sort_order: lazyParams.sortOrder,
          search: globalFilter,
          ...lazyParams.filters
        }
      });
      setClients(response.data.data);
      setTotalRecords(response.data.total);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching clients:', error);
      showToast('error', t('error_fetching_clients'));
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await api.get('/clients/statistics');
      setTotalClients(response.data.total_clients);
      setTotalVehicles(response.data.total_vehicles);
      setTotalOrders(response.data.total_orders);
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  const onPage = (page: number) => {
    setLazyParams(prevState => ({ ...prevState, page, first: page * lazyParams.rows }));
  };

  const onSort = (field: string) => {
    const sortOrder = lazyParams.sortField === field && lazyParams.sortOrder === 'asc' ? 'desc' : 'asc';
    setLazyParams(prevState => ({ ...prevState, sortField: field, sortOrder }));
  };

  const onFilter = (field: string, value: string) => {
    setLazyParams(prevState => ({
      ...prevState,
      first: 0,
      page: 1,
      filters: { ...prevState.filters, [field]: value }
    }));
  };

  const onGlobalFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLazyParams(prevState => ({
      ...prevState,
      first: 0,
      page: 1,
      filters: { ...prevState.filters, global: value }
    }));
    setGlobalFilter(value);
  };

  const openNew = () => {
    setEditingClient({});
    setDialogVisible(true);
  };

  const hideDialog = () => {
    setDialogVisible(false);
    setEditingClient(null);
  };

  const saveClient = async () => {
    if (!editingClient) return;

    try {
      if (editingClient.id) {
        await api.put(`/clients/${editingClient.id}`, editingClient);
        showToast('success', t('client_updated'));
      } else {
        await api.post('/clients', editingClient);
        showToast('success', t('client_created'));
      }
      hideDialog();
      fetchClients();
      fetchStatistics();
    } catch (error) {
      console.error('Error saving client:', error);
      showToast('error', t('error_saving_client'));
    }
  };

  const editClient = (client: Client) => {
    setEditingClient({ ...client });
    setDialogVisible(true);
  };

  const confirmDeleteClient = (client: Client) => {
    setClientToDelete(client);
    setDeleteDialogVisible(true);
  };

  const deleteClient = async () => {
    if (!clientToDelete) return;

    try {
      await api.delete(`/clients/${clientToDelete.id}`);
      showToast('success', t('client_deleted'));
      setDeleteDialogVisible(false);
      fetchClients();
      fetchStatistics();
    } catch (error) {
      console.error('Error deleting client:', error);
      showToast('error', t('error_deleting_client'));
    }
  };

  const showToast = (severity: string, message: string) => {
    setToast({ visible: true, severity, message });
    setTimeout(() => setToast({ ...toast, visible: false }), 3000);
  };

  return (
    <div className="p-6 min-h-screen bg-gray-100">
      {toast.visible && (
        <div className={`fixed top-4 right-4 p-4 rounded-md shadow-md ${
          toast.severity === 'success' ? 'bg-green-500' : 'bg-red-500'
        } text-white`}>
          {toast.message}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex justify-between items-center">
            <h6 className="text-sm font-semibold text-gray-600">{t('total_clients')}</h6>
            <Users className="text-blue-500" size={24} />
          </div>
          <p className="text-2xl font-bold mt-2">{totalClients}</p>
        </div>
        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex justify-between items-center">
            <h6 className="text-sm font-semibold text-gray-600">{t('total_vehicles')}</h6>
            <Car className="text-green-500" size={24} />
          </div>
          <p className="text-2xl font-bold mt-2">{totalVehicles}</p>
        </div>
        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex justify-between items-center">
            <h6 className="text-sm font-semibold text-gray-600">{t('total_orders')}</h6>
            <FileText className="text-yellow-500" size={24} />
          </div>
          <p className="text-2xl font-bold mt-2">{totalOrders}</p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-800">{t('manage_clients')}</h2>
            <div className="flex items-center">
              <div className="relative mr-2">
                <input
                  type="text"
                  className="pl-10 pr-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={t('search_clients')}
                  value={globalFilter}
                  onChange={onGlobalFilterChange}
                />
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              </div>
              <button
                onClick={openNew}
                className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition duration-200"
              >
                <Plus size={20} className="mr-2" />
                {t('new_client')}
              </button>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {['first_name', 'last_name', 'email', 'phone', 'vehicles_count', 'service_orders_count'].map((field) => (
                  <th
                    key={field}
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => onSort(field)}
                  >
                    {t(field)}
                    {lazyParams.sortField === field && (
                      <span className="ml-2">
                        {lazyParams.sortOrder === 'asc' ? '▲' : '▼'}
                      </span>
                    )}
                  </th>
                ))}
                <th scope="col" className="relative px-6 py-3">
                  <span className="sr-only">{t('actions')}</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center">{t('loading')}</td>
                </tr>
              ) : clients.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center">{t('no_clients_found')}</td>
                </tr>
              ) : (
                clients.map((client) => (
                  <tr key={client.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">{client.first_name}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{client.last_name}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{client.email}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{client.phone}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">{client.vehicles_count}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">{client.service_orders_count}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => editClient(client)}
                        className="text-indigo-600 hover:text-indigo-900 mr-2"
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        onClick={() => confirmDeleteClient(client)}
                        className="text-red-600 hover:text-red-900 mr-2"
                      >
                        <Trash2 size={18} />
                      </button>
                      <button
                        onClick={() => navigate(`/clients/${client.id}`)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        <Eye size={18} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex justify-between items-center p-4 border-t border-gray-200">
          <span className="text-sm text-gray-700">
            {t('showing')} {lazyParams.first + 1} - {Math.min(lazyParams.first + lazyParams.rows, totalRecords)} {t('of')} {totalRecords}
          </span>
          <div className="flex items-center">
            <button
              onClick={() => onPage(1)}
              disabled={lazyParams.page === 1}
              className="p-2 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 disabled:opacity-50"
            >
              <ChevronsLeft size={20} />
            </button>
            <button
              onClick={() => onPage(lazyParams.page - 1)}
              disabled={lazyParams.page === 1}
              className="p-2 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 disabled:opacity-50"
            >
              <ChevronLeft size={20} />
            </button>
            <span className="mx-4 text-sm">
              {t('page')} {lazyParams.page} {t('of')} {Math.ceil(totalRecords / lazyParams.rows)}
            </span>
            <button
              onClick={() => onPage(lazyParams.page + 1)}
              disabled={lazyParams.page === Math.ceil(totalRecords / lazyParams.rows)}
              className="p-2 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 disabled:opacity-50"
            >
              <ChevronRight size={20} />
            </button>
            <button
              onClick={() => onPage(Math.ceil(totalRecords / lazyParams.rows))}
              disabled={lazyParams.page === Math.ceil(totalRecords / lazyParams.rows)}
              className="p-2 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 disabled:opacity-50"
            >
              <ChevronsRight size={20} />
            </button>
          </div>
        </div>
      </div>

      {dialogVisible && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-xl font-semibold">{editingClient && editingClient.id ? t('edit_client') : t('new_client')}</h2>
              <button onClick={hideDialog} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); saveClient(); }} className="p-6">
              <div className="space-y-4">
                <div>
                  <label htmlFor="first_name" className="block text-sm font-medium text-gray-700">{t('first_name')}</label>
                  <input
                    type="text"
                    id="first_name"
                    value={editingClient?.first_name || ''}
                    onChange={(e) => setEditingClient(prev => prev ? { ...prev, first_name: e.target.value } : null)}
                    required
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  />
                </div>
                <div>
                  <label htmlFor="last_name" className="block text-sm font-medium text-gray-700">{t('last_name')}</label>
                  <input
                    type="text"
                    id="last_name"
                    value={editingClient?.last_name || ''}
                    onChange={(e) => setEditingClient(prev => prev ? { ...prev, last_name: e.target.value } : null)}
                    required
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">{t('email')}</label>
                  <input
                    type="email"
                    id="email"
                    value={editingClient?.email || ''}
                    onChange={(e) => setEditingClient(prev => prev ? { ...prev, email: e.target.value } : null)}
                    required
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700">{t('phone')}</label>
                  <input
                    type="tel"
                    id="phone"
                    value={editingClient?.phone || ''}
                    onChange={(e) => setEditingClient(prev => prev ? { ...prev, phone: e.target.value } : null)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  />
                </div>
                <div>
                  <label htmlFor="address" className="block text-sm font-medium text-gray-700">{t('address')}</label>
                  <input
                    type="text"
                    id="address"
                    value={editingClient?.address || ''}
                    onChange={(e) => setEditingClient(prev => prev ? { ...prev, address: e.target.value } : null)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={hideDialog}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  {t('save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteDialogVisible && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">{t('confirm')}</h3>
              <p className="text-sm text-gray-500 mb-4">
                {clientToDelete ? t('delete_client_confirmation', { name: `${clientToDelete.first_name} ${clientToDelete.last_name}` }) : ''}
              </p>
              <div className="mt-4 flex justify-end space-x-3">
                <button
                  type="button"
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  onClick={() => setDeleteDialogVisible(false)}
                >
                  {t('cancel')}
                </button>
                <button
                  type="button"
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  onClick={deleteClient}
                >
                  {t('delete')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

